import { useCallback, useMemo, useState } from "react";

import { useGetIdentity } from "@refinedev/core";

import { IUser } from "@/types";
import { formatters } from "@/utils";
import {
  ADMIN_GET_USER_NOTIFICATION_PREFERENCES_QUERY,
  ADMIN_UPDATE_USER_NOTIFICATION_PREFERENCE_MUTATION,
  type AdminGetUserNotificationPreferencesData,
  type AdminUpdateUserNotificationPreferenceData,
  GET_USER_NOTIFICATION_PREFERENCES_QUERY,
  GET_VITALL_PRACTITIONER_QUERY,
  type GetUserNotificationPreferencesData,
  type GetUserNotificationsInput,
  type GetVitallPractitionerData,
  type GetVitallPractitionerInput,
  type NotificationSetting,
  UPDATE_USER_NOTIFICATION_PREFERENCE_MUTATION,
  type UpdateNotificationPreferenceInput,
  type UpdateUserNotificationPreferenceData,
} from "@/utils/GraphQLClient";

import { useMutation, useQuery } from "urql";

/** Engage notification-preference identifiers for medly alert emails. */
export const MEDLY_ALL_ALERTS_IDENTIFIER = "medly:all_alerts";
export const MEDLY_RED_ALERTS_IDENTIFIER = "medly:red_alerts";

export enum AlertEmailPreference {
  ALL_ALERTS = "all_alerts",
  RED_ALERTS = "red_alerts",
  NONE = "none",
}

/** Per-identifier email_enabled flags for a given preference. */
export interface AlertPreferenceFlags {
  allEnabled: boolean;
  redEnabled: boolean;
}

/**
 * Maps an {@link AlertEmailPreference} to the two medly identifier flags:
 * ALL_ALERTS → both on, RED_ALERTS → red only, NONE → both off.
 */
export const alertPreferenceToFlags = (preference: AlertEmailPreference): AlertPreferenceFlags => ({
  allEnabled: preference === AlertEmailPreference.ALL_ALERTS,
  redEnabled: preference === AlertEmailPreference.ALL_ALERTS || preference === AlertEmailPreference.RED_ALERTS,
});

/** Reads the raw per-identifier email_enabled flags from a settings response. */
export const alertSettingsToFlags = (settings: NotificationSetting[] | undefined): AlertPreferenceFlags => ({
  allEnabled: settings?.find(s => s.identifier === MEDLY_ALL_ALERTS_IDENTIFIER)?.preference.email_enabled ?? false,
  redEnabled: settings?.find(s => s.identifier === MEDLY_RED_ALERTS_IDENTIFIER)?.preference.email_enabled ?? false,
});

/**
 * Derives the current preference from a notification-settings response.
 * `hasPermission` is false when neither medly alert identifier is configured.
 */
export const deriveAlertPreference = (
  settings: NotificationSetting[] | undefined
): { preference: AlertEmailPreference; hasPermission: boolean } => {
  const allAlerts = settings?.find(s => s.identifier === MEDLY_ALL_ALERTS_IDENTIFIER);
  const redAlerts = settings?.find(s => s.identifier === MEDLY_RED_ALERTS_IDENTIFIER);

  if (!allAlerts && !redAlerts) {
    return { preference: AlertEmailPreference.NONE, hasPermission: false };
  }

  const allAlertsEnabled = allAlerts?.preference.email_enabled ?? false;
  const redAlertsEnabled = redAlerts?.preference.email_enabled ?? false;

  let preference: AlertEmailPreference;
  if (allAlertsEnabled && redAlertsEnabled) {
    preference = AlertEmailPreference.ALL_ALERTS;
  } else if (redAlertsEnabled) {
    preference = AlertEmailPreference.RED_ALERTS;
  } else {
    preference = AlertEmailPreference.NONE;
  }
  return { preference, hasPermission: true };
};

/** Canonical alert email-preference dropdown options (Title-Case labels). */
export const getAlertPrefOptions = (
  translate: (key: string) => string
): { value: AlertEmailPreference; label: string }[] => [
  { value: AlertEmailPreference.ALL_ALERTS, label: translate("common.alertEmailPreference.allAlerts") },
  { value: AlertEmailPreference.RED_ALERTS, label: translate("common.alertEmailPreference.redAlerts") },
  { value: AlertEmailPreference.NONE, label: translate("common.alertEmailPreference.none") },
];

type PreferenceMutationRunner = (vars: { input: UpdateNotificationPreferenceInput }) => Promise<{ error?: unknown }>;

/**
 * Diff-writes the two medly identifiers for `userId`: only the identifiers whose
 * desired value differs from `settings` are written. Sequential, short-circuits
 * on the first error. Shared by the self and admin hooks (they differ only in
 * which query/mutation they bind in).
 *
 * @returns `success` (all writes ok / nothing to do) and `changed` (whether any
 *   mutation was sent — lets the caller decide whether to refetch).
 */
export const writeAlertPreference = async (
  executeMutation: PreferenceMutationRunner,
  userId: string,
  settings: NotificationSetting[] | undefined,
  preference: AlertEmailPreference
): Promise<{ success: boolean; changed: boolean }> => {
  const current = alertSettingsToFlags(settings);
  const desired = alertPreferenceToFlags(preference);

  const updates: { identifier: string; email_enabled: boolean }[] = [];
  if (desired.allEnabled !== current.allEnabled) {
    updates.push({ identifier: MEDLY_ALL_ALERTS_IDENTIFIER, email_enabled: desired.allEnabled });
  }
  if (desired.redEnabled !== current.redEnabled) {
    updates.push({ identifier: MEDLY_RED_ALERTS_IDENTIFIER, email_enabled: desired.redEnabled });
  }
  if (updates.length === 0) {
    return { success: true, changed: false };
  }

  for (const update of updates) {
    const result = await executeMutation({ input: { ...update, user_id: userId } });
    if (result.error) {
      return { success: false, changed: true };
    }
  }
  return { success: true, changed: true };
};

export interface UseAlertEmailPreferenceResult {
  /** Current preference from the server*/
  preference: AlertEmailPreference;
  /** True during initial fetch */
  isLoading: boolean;
  /** True during an update call */
  isUpdating: boolean;
  /** Error message if the last operation failed */
  error: string | null;
  /** False when medly alert notification types are not configured for this user */
  hasPermission: boolean;
  /** Update the preference on the server. Returns true on success. */
  updatePreference: (preference: AlertEmailPreference) => Promise<boolean>;
}

export const useAlertEmailPreference = (): UseAlertEmailPreferenceResult => {
  const { data: user, isLoading: isIdentityLoading } = useGetIdentity<IUser>();
  const userId = user?.id ? String(user.id) : undefined;
  const [isUpdating, setIsUpdating] = useState(false);

  const [queryResult, reexecuteQuery] = useQuery<
    GetUserNotificationPreferencesData,
    { input: GetUserNotificationsInput }
  >({
    query: GET_USER_NOTIFICATION_PREFERENCES_QUERY,
    variables: { input: { user_id: userId ?? "" } },
    pause: !userId,
  });

  const [, executeMutation] = useMutation<
    UpdateUserNotificationPreferenceData,
    { input: UpdateNotificationPreferenceInput }
  >(UPDATE_USER_NOTIFICATION_PREFERENCE_MUTATION);

  // Derive preference and permission from the notification settings
  const { preference, hasPermission } = useMemo(
    () => deriveAlertPreference(queryResult.data?.getUserNotificationPreferences),
    [queryResult.data]
  );

  const error = useMemo(() => {
    const err = queryResult.error;
    if (!err) {
      return null;
    }
    return err.graphQLErrors?.[0]?.message ?? err.message ?? "An error occurred";
  }, [queryResult.error]);

  const updatePreference = useCallback(
    async (newPreference: AlertEmailPreference): Promise<boolean> => {
      if (!userId) {
        return false;
      }

      setIsUpdating(true);
      try {
        const { success, changed } = await writeAlertPreference(
          executeMutation,
          userId,
          queryResult.data?.getUserNotificationPreferences,
          newPreference
        );
        // Refetch to get updated state only when something actually changed.
        if (success && changed) {
          reexecuteQuery({ requestPolicy: "network-only" });
        }
        return success;
      } finally {
        setIsUpdating(false);
      }
    },
    [userId, queryResult.data, executeMutation, reexecuteQuery]
  );

  return {
    preference,
    isLoading: isIdentityLoading || queryResult.fetching,
    isUpdating,
    error,
    hasPermission,
    updatePreference,
  };
};

export interface UseAdminAlertEmailPreferenceResult {
  /**
   * Current alert email preference for the target practitioner. `NONE` while the
   * lookup is in flight or when no medly alert config is provisioned.
   */
  preference: AlertEmailPreference;
  /** True until the target practitioner's user_id and current preferences have loaded. */
  isResolving: boolean;
  /** False when the target practitioner has no medly alert notification config. */
  hasPermission: boolean;
  /**
   * Persist the alert email preference for the target (covered) practitioner.
   * Reads the practitioner's current settings and only writes the identifiers
   * that differ from `preference`. Returns true on success (including no-ops),
   * false if the user_id cannot be resolved or a mutation fails.
   */
  updatePreference: (preference: AlertEmailPreference) => Promise<boolean>;
}

/**
 * Admin-on-behalf-of variant of {@link useAlertEmailPreference}. Lets an org
 * admin set another practitioner's alert email preference — used by the coverage
 * flow when assigning/removing coverage for a practitioner who is not the
 * logged-in user. Resolves the practitioner's Engage user_id from their FHIR id,
 * reads their current preferences, and diff-writes only what changed.
 *
 * @param fhirPractitionerId Bare FHIR Practitioner id of the covered
 *   practitioner (e.g. "123"), or null when no target is selected.
 */
export const useAdminAlertEmailPreference = (fhirPractitionerId: string | null): UseAdminAlertEmailPreferenceResult => {
  const [{ data: vitallData, fetching: vitallFetching }] = useQuery<
    GetVitallPractitionerData,
    { input: GetVitallPractitionerInput }
  >({
    query: GET_VITALL_PRACTITIONER_QUERY,
    variables: {
      input: {
        fhir_practitioner_id: fhirPractitionerId ? formatters.formatPractitioner(fhirPractitionerId) : "",
      },
    },
    pause: !fhirPractitionerId,
  });

  const userId = useMemo(() => vitallData?.getVitallPractitioner?.user_id, [vitallData]);

  const [{ data: prefsData, fetching: prefsFetching }] = useQuery<
    AdminGetUserNotificationPreferencesData,
    { input: GetUserNotificationsInput }
  >({
    query: ADMIN_GET_USER_NOTIFICATION_PREFERENCES_QUERY,
    variables: { input: { user_id: userId ?? "" } },
    pause: !userId,
  });

  const settings = prefsData?.adminGetUserNotificationPreferences;
  const { preference, hasPermission } = useMemo(() => deriveAlertPreference(settings), [settings]);

  const [, executeMutation] = useMutation<
    AdminUpdateUserNotificationPreferenceData,
    { input: UpdateNotificationPreferenceInput }
  >(ADMIN_UPDATE_USER_NOTIFICATION_PREFERENCE_MUTATION);

  // True while the lookup chain is still in flight for the current target — used to
  // gate the confirm action so a write never races the lookup (which would drop the
  // preference or, with urql's stale cache, target the previous practitioner). Once
  // the vitall lookup settles without a user_id (no Engage user, or an errored/empty
  // response), there is nothing left to resolve, so this settles to false rather than
  // hanging — otherwise Confirm would stay disabled forever for such a practitioner.
  const isResolving = useMemo(
    () => !!fhirPractitionerId && (vitallFetching || (!!userId && (prefsFetching || !prefsData))),
    [fhirPractitionerId, vitallFetching, userId, prefsFetching, prefsData]
  );

  const updatePreference = useCallback(
    async (preference: AlertEmailPreference): Promise<boolean> => {
      if (!userId) {
        return false;
      }
      // No medly alert config provisioned for this practitioner — nothing to set.
      if (!hasPermission) {
        return true;
      }
      const { success } = await writeAlertPreference(executeMutation, userId, settings, preference);
      return success;
    },
    [userId, hasPermission, settings, executeMutation]
  );

  return { preference, isResolving, hasPermission, updatePreference };
};
